import {
  QBox,
  QButton,
  QFormControl,
  QHeading,
  QIconButton,
  QInput,
  QRadio,
  QStack,
  QText,
} from '@qualio/ui-components';
import React, { useCallback } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { TrainingAssessmentForm } from './types/trainingAssessment';

type TrainingAssessmentFormQuestionProps = {
  index: number;
};

export const TrainingAssessmentFormQuestion: React.FC<
  TrainingAssessmentFormQuestionProps
> = ({ index }) => {
  const { t } = useTranslation();
  const { setValue } = useFormContext();
  const { fields, append, remove, move } =
    useFieldArray<TrainingAssessmentForm>({
      name: `questions.${index}.answers`,
    });

  const setCorrectAnswer = useCallback(
    (correctIndex: number) => {
      for (let item = 0; item < fields.length; item++) {
        setValue(
          `questions.${index}.answers.${item}.is_correct`,
          item === correctIndex,
          { shouldDirty: true },
        );
      }
    },
    [fields, setValue, index],
  );

  return (
    <QStack gap={3} data-cy="training-assessment-form-question">
      <QHeading size="sm" color="gray.900">
        {t('trainingAssessment.questionNumber', { number: index + 1 })}
      </QHeading>
      <QFormControl label={t('labels.question')}>
        <Controller
          render={({ field: { onChange, value } }) => (
            <QInput
              onChange={onChange}
              value={value}
              placeholder={t('forms.enterQuestion')}
              data-cy="training-assessment-question-title"
            />
          )}
          name={`questions.${index}.question`}
        />
      </QFormControl>

      <QStack gap={1} marginTop="16px">
        <QHeading size="xs" color="gray.700">
          {t('trainingAssessment.options')}
        </QHeading>
        <QText fontSize="xs" color="gray.500">
          {t('trainingAssessment.pleaseChooseCorrectAnswer')}
        </QText>
        <QStack direction="column" gap="2">
          {fields.map((field, answerIndex) => (
            <QStack
              direction="row"
              key={field.stableKey ?? field.id}
              data-cy="training-assessment-answers"
            >
              <Controller
                name={`questions.${index}.answers.${answerIndex}.is_correct`}
                render={({ field: { value } }) => (
                  <QRadio
                    onChange={() => setCorrectAnswer(answerIndex)}
                    isChecked={value}
                  />
                )}
              />
              <Controller
                name={`questions.${index}.answers.${answerIndex}.answer`}
                render={({ field: { onChange, value } }) => (
                  <QBox flexGrow={1} minWidth={0}>
                    <QInput
                      onChange={onChange}
                      value={value}
                      placeholder={t('labels.option', {
                        number: answerIndex + 1,
                      })}
                    />
                  </QBox>
                )}
              />
              <QIconButton
                iconName="ArrowUp"
                aria-label={t('buttons.moveUp')}
                isDisabled={answerIndex === 0}
                onClick={() => move(answerIndex, answerIndex - 1)}
              />
              <QIconButton
                iconName="ArrowDown"
                aria-label={t('buttons.moveDown')}
                isDisabled={answerIndex === fields.length - 1}
                onClick={() => move(answerIndex, answerIndex + 1)}
              />
              <QIconButton
                iconName="Trash"
                aria-label={t('buttons.remove')}
                data-cy="remove-answer"
                isDisabled={fields.length <= 2}
                onClick={() => remove(answerIndex)}
              />
            </QStack>
          ))}
          <QButton
            variant="ghost"
            leftIcon="Plus"
            data-cy="add-answer"
            onClick={() =>
              append({
                answer: '',
                stableKey: crypto.randomUUID(),
                is_correct: false,
              })
            }
          >
            {t('buttons.addOption')}
          </QButton>
        </QStack>
      </QStack>
    </QStack>
  );
};
